import type { PublicUser, Site, SiteStatus, User, Visit } from './types';
import { apiToken, trackingCode, uid } from './id';
import { parseUserAgent } from './ua';
import { fakeIp, resolveGeo } from './geo';
import { normalizeVisitUrl } from './source';

const KEYS = {
  users: 'sf_users',
  sites: 'sf_sites',
  visits: 'sf_visits',
  session: 'sf_session',
  currentSite: 'sf_current_site',
  ready: 'sf_ready',
};

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function write<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value));
}

function normalizeUser(user: User): User {
  return {
    ...user,
    apiToken: user.apiToken || apiToken(),
  };
}

function normalizeSite(site: Site): Site {
  return {
    ...site,
    status: site.status || 'active',
  };
}

export function getUsers(): User[] {
  const raw = read<User[]>(KEYS.users, []);
  let changed = false;
  const users = raw.map((user) => {
    if (user.apiToken) return normalizeUser(user);
    changed = true;
    return normalizeUser({ ...user, apiToken: apiToken() });
  });
  if (changed) write(KEYS.users, users);
  return users;
}

export function saveUsers(users: User[]) {
  write(KEYS.users, users);
}

export function getSites(): Site[] {
  const raw = read<Site[]>(KEYS.sites, []);
  let changed = false;
  const sites = raw.map((site) => {
    if (site.status) return normalizeSite(site);
    changed = true;
    return normalizeSite(site);
  });
  if (changed) write(KEYS.sites, sites);
  return sites;
}

export function saveSites(sites: Site[]) {
  write(KEYS.sites, sites);
}

export function getVisits(): Visit[] {
  return read<Visit[]>(KEYS.visits, []);
}

export function saveVisits(visits: Visit[]) {
  let next = visits.slice(0, 5000);
  for (let i = 0; i < 8; i++) {
    try {
      write(KEYS.visits, next);
      return;
    } catch {
      next = next.slice(0, Math.max(50, Math.floor(next.length * 0.6)));
    }
  }
  try {
    localStorage.removeItem(KEYS.visits);
  } catch {
    /* ignore */
  }
}

export function getSessionUserId(): string | null {
  return localStorage.getItem(KEYS.session);
}

export function setSessionUserId(userId: string | null) {
  if (!userId) localStorage.removeItem(KEYS.session);
  else localStorage.setItem(KEYS.session, userId);
}

export function getCurrentSiteId(): string | null {
  return localStorage.getItem(KEYS.currentSite);
}

export function setCurrentSiteId(siteId: string | null) {
  if (!siteId) localStorage.removeItem(KEYS.currentSite);
  else localStorage.setItem(KEYS.currentSite, siteId);
}

export function isStoreReady(): boolean {
  return localStorage.getItem(KEYS.ready) === '1';
}

export function markStoreReady() {
  localStorage.setItem(KEYS.ready, '1');
}

export function toPublicUser(user: User): PublicUser {
  const next = normalizeUser(user);
  return {
    id: next.id,
    email: next.email,
    createdAt: next.createdAt,
    apiToken: next.apiToken,
    displayName: next.displayName,
  };
}

export function getUserByEmail(email: string): User | undefined {
  return getUsers().find((u) => u.email.toLowerCase() === email.toLowerCase());
}

export function getUserById(id: string): User | undefined {
  return getUsers().find((u) => u.id === id);
}

export function addUser(user: User) {
  saveUsers([...getUsers(), user]);
}

export function getSitesByUser(userId: string): Site[] {
  return getSites()
    .filter((s) => s.userId === userId)
    .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
}

export function getSiteById(id: string): Site | undefined {
  return getSites().find((s) => s.id === id);
}

export function getSiteByCode(code: string): Site | undefined {
  return getSites().find((s) => s.trackingCode === code);
}

export function createSite(
  userId: string,
  name: string,
  domain: string,
  extra?: { industry?: string; status?: SiteStatus }
): Site {
  if (!name.trim()) throw new Error('请填写站点名称');
  const normalized = assertValidDomain(domain);
  if (getSitesByUser(userId).some((s) => s.domain === normalized)) {
    throw new Error('该域名已添加');
  }
  const site: Site = {
    id: uid(),
    userId,
    name: name.trim(),
    domain: normalized,
    trackingCode: trackingCode(),
    createdAt: new Date().toISOString(),
    status: extra?.status || 'pending',
    industry: extra?.industry || '',
  };
  saveSites([...getSites(), site]);
  setCurrentSiteId(site.id);
  return site;
}

export function updateSite(
  id: string,
  patch: Partial<Pick<Site, 'name' | 'domain' | 'status' | 'industry' | 'lastCollectAt'>>
): Site {
  const sites = getSites();
  const index = sites.findIndex((s) => s.id === id);
  if (index < 0) throw new Error('站点不存在');
  const next = { ...sites[index], ...patch };
  if (patch.domain) {
    next.domain = assertValidDomain(patch.domain);
    const dup = sites.find(
      (s) => s.id !== id && s.userId === next.userId && s.domain === next.domain
    );
    if (dup) throw new Error('该域名已添加');
  }
  if (patch.name) next.name = patch.name.trim();
  sites[index] = next;
  saveSites(sites);
  return next;
}

export function markSiteCollected(id: string) {
  const site = getSiteById(id);
  if (!site || site.status === 'disabled') return;
  updateSite(id, {
    status: 'active',
    lastCollectAt: new Date().toISOString(),
  });
}

export function clearSiteVisits(id: string) {
  const site = getSiteById(id);
  saveVisits(
    getVisits().filter((v) => v.siteId !== id && v.trackingCode !== site?.trackingCode)
  );
}

export function deleteSite(id: string) {
  const site = getSiteById(id);
  saveSites(getSites().filter((s) => s.id !== id));
  saveVisits(
    getVisits().filter((v) => v.siteId !== id && v.trackingCode !== site?.trackingCode)
  );
  if (getCurrentSiteId() === id) {
    const rest = site ? getSitesByUser(site.userId) : [];
    setCurrentSiteId(rest[0]?.id ?? null);
  }
}

export function getVisitsBySite(siteId: string): Visit[] {
  const site = getSiteById(siteId);
  return getVisits()
    .filter((v) => v.siteId === siteId || (site && v.trackingCode === site.trackingCode))
    .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
}

export function mergeVisits(incoming: Visit[]) {
  if (!incoming.length) return;
  const map = new Map(getVisits().map((v) => [v.id, v]));
  incoming.forEach((visit) => {
    if (!visit.id) return;
    const prev = map.get(visit.id);
    map.set(
      visit.id,
      prev
        ? {
            ...prev,
            ...visit,
            siteId: prev.siteId || visit.siteId,
            durationMs: Math.max(prev.durationMs || 0, visit.durationMs || 0),
            province: visit.province || prev.province,
            city: visit.city || prev.city,
            isp: visit.isp || prev.isp,
          }
        : visit
    );
  });
  const merged = [...map.values()].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
  saveVisits(merged.slice(0, 20000));
}

export type IncomingVisit = {
  trackingCode?: string;
  siteId?: string;
  visitorId: string;
  pageUrl: string;
  referrer?: string;
  userAgent?: string;
  ip?: string;
  country?: string;
  city?: string;
  province?: string;
  isp?: string;
  durationMs?: number;
  createdAt?: string;
  id?: string;
  language?: string;
  screen?: string;
};

export function recordVisit(input: IncomingVisit): Visit | null {
  const site = input.siteId
    ? getSiteById(input.siteId)
    : input.trackingCode
      ? getSiteByCode(input.trackingCode)
      : undefined;
  if (!site) return null;
  if (site.status === 'disabled') return null;
  markSiteCollected(site.id);

  const ua = input.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const parsed = parseUserAgent(ua);
  const tz =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined;
  const lang = typeof navigator !== 'undefined' ? navigator.language : undefined;
  const geo = resolveGeo(tz, lang);

  const visit: Visit = {
    id: input.id || uid(),
    siteId: site.id,
    trackingCode: site.trackingCode,
    visitorId: input.visitorId,
    ip: input.ip || fakeIp(input.city || geo.city),
    pageUrl: normalizeVisitUrl(input.pageUrl || '/'),
    referrer: input.referrer || '',
    userAgent: ua,
    country: input.country || geo.country,
    city: input.city || geo.city,
    province: input.province || '',
    isp: input.isp || '',
    device: parsed.device,
    browser: parsed.browser,
    os: parsed.os,
    durationMs: input.durationMs || 0,
    createdAt: input.createdAt || new Date().toISOString(),
    language: input.language || lang || '',
    screen: input.screen || '',
  };

  saveVisits([visit, ...getVisits()].slice(0, 20000));
  return visit;
}

export function normalizeDomain(domain: string): string {
  return domain
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .replace(/:\d+$/, '')
    .replace(/\.$/, '')
    .toLowerCase();
}

export function assertValidDomain(domain: string): string {
  const normalized = normalizeDomain(domain);
  if (!normalized) throw new Error('请填写有效域名');
  if (normalized === 'localhost' || normalized.endsWith('.localhost')) return normalized;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(normalized)) return normalized;
  if (!/^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/i.test(normalized)) {
    throw new Error('域名格式不正确，例如 example.com');
  }
  return normalized;
}

export function updateUser(
  id: string,
  patch: Partial<Pick<User, 'passwordHash' | 'apiToken' | 'displayName'>>
): User {
  const users = getUsers();
  const index = users.findIndex((u) => u.id === id);
  if (index < 0) throw new Error('用户不存在');
  users[index] = { ...users[index], ...patch };
  saveUsers(users);
  return users[index];
}

export function rotateApiToken(userId: string): string {
  const token = apiToken();
  updateUser(userId, { apiToken: token });
  return token;
}

export function getUserByToken(token: string): User | undefined {
  if (!token) return undefined;
  return getUsers().find((u) => u.apiToken === token);
}

export function upsertLocalUser(user: User) {
  const users = getUsers();
  const index = users.findIndex((u) => u.email === user.email || u.id === user.id);
  if (index >= 0) users[index] = { ...users[index], ...user };
  else users.push(user);
  saveUsers(users);
  return users.find((u) => u.email === user.email)!;
}

export function mergeRemoteSites(userId: string, incoming: Site[]) {
  if (!incoming.length) return getSitesByUser(userId);
  const all = getSites();
  incoming.forEach((site) => {
    const index = all.findIndex(
      (s) => s.id === site.id || (s.userId === userId && s.domain === site.domain)
    );
    const next = { ...site, userId };
    if (index >= 0) all[index] = { ...all[index], ...next };
    else all.push(next);
  });
  saveSites(all);
  return getSitesByUser(userId);
}

export function replaceUserSites(userId: string, incoming: Site[]) {
  const others = getSites().filter((s) => s.userId !== userId);
  const next = incoming.map((site) => ({ ...site, userId }));
  saveSites([...others, ...next]);
  const current = getCurrentSiteId();
  if (current && !next.some((s) => s.id === current)) {
    setCurrentSiteId(next[0]?.id ?? null);
  }
  return getSitesByUser(userId);
}

export function domainMatchesHost(domain: string, host: string): boolean {
  const d = normalizeDomain(domain);
  const h = host.toLowerCase().replace(/:\d+$/, '');
  return d === h || h.endsWith(`.${d}`);
}
